import { useTranslations } from "adapters/i18n";
import { type ChangeEvent, useCallback, useMemo, useState } from "react";
import type { EnrichedHerdRecord } from "shared/types";
import type { MovementFormState } from "../movements.types";
import {
  formatCarcassYieldInput,
  getLocalCarcassYield,
  parseCarcassYield,
  RecordType,
} from "../utils/movements.utils";
import {
  CarcassYieldError,
  validateAnimalCategory,
  validateCarcassYield,
  validateOperationDate,
  validateOperationType,
  validateQuantity,
  validateRecordType,
  validateWeight,
} from "../utils/movements.validators";
import { DateUtils } from "shared/dateUtils";

type UseMovementsFormProps = {
  onSubmit: (formState: MovementFormState) => void;
  selectedHerdRecord: EnrichedHerdRecord | null;
};

export function useMovementsForm({
  onSubmit,
  selectedHerdRecord,
}: UseMovementsFormProps) {
  const { t } = useTranslations();

  const [formState, setFormState] = useState<MovementFormState>({
    animalCategoryId: {
      value: selectedHerdRecord?.animalCategoryId ?? null,
      error: null,
    },
    carcassYield: {
      value: getLocalCarcassYield(selectedHerdRecord?.carcassYield),
      error: null,
    },
    operationDate: {
      value: selectedHerdRecord?.operationDate ?? DateUtils.now().toISOString(),
      error: null,
    },
    operationTypeId: {
      value: selectedHerdRecord?.operationTypeId ?? null,
      error: null,
    },
    quantity: { value: selectedHerdRecord?.quantity ?? null, error: null },
    recordTypeId: {
      value:
        (selectedHerdRecord?.recordTypeId as RecordType) ?? RecordType.Entry,
      error: null,
    },
    weight: { value: selectedHerdRecord?.weight ?? null, error: null },
  });

  const carcassYieldErrorToMessage: Record<CarcassYieldError, string> = {
    [CarcassYieldError.OutOfRange]: t("livestock:max-carcass-yield-message"),
  };

  const handleChangeCategory = (value: number) => {
    setFormState((prevState) => ({
      ...prevState,
      animalCategoryId: { value, error: null },
    }));
  };

  const handleChangeCarcassYield = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value !== "" ? event.target.value : null;
    const formattedValue = formatCarcassYieldInput(value);
    const parsedValue = parseCarcassYield(formattedValue);

    if (Number.isNaN(parsedValue)) {
      return;
    }

    const { error } = validateCarcassYield(parsedValue);
    const errorMessage =
      error != null ? carcassYieldErrorToMessage[error] : null;

    setFormState((prevState) => ({
      ...prevState,
      carcassYield: { value: formattedValue, error: errorMessage },
    }));
  };

  const handleChangeOperationDate = (value: string | null) => {
    setFormState((prevState) => ({
      ...prevState,
      operationDate: { value, error: null },
    }));
  };

  const handleChangeRecordType = (value: RecordType) => {
    setFormState((prevState) => ({
      ...prevState,
      recordTypeId: { value, error: null },
      operationTypeId: { value: null, error: null },
      carcassYield: { value: null, error: null },
    }));
  };

  const handleChangeOperationType = (value: number) => {
    setFormState((prevState) => ({
      ...prevState,
      operationTypeId: { value, error: null },
    }));
  };

  const handleChangeQuantity = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value !== "" ? Number(event.target.value) : null;

    if (value !== null && (Number.isNaN(value) || !Number.isInteger(value))) {
      return;
    }

    setFormState((prevState) => ({
      ...prevState,
      quantity: { value, error: null },
    }));
  };

  const handleChangeWeight = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value !== "" ? Number(event.target.value) : null;

    if (value !== null && (Number.isNaN(value) || !Number.isInteger(value))) {
      return;
    }

    setFormState((prevState) => ({
      ...prevState,
      weight: { value, error: null },
    }));
  };

  const handleResetForm = () => {
    setFormState({
      animalCategoryId: { value: null, error: null },
      carcassYield: { value: null, error: null },
      operationDate: { value: DateUtils.now().toISOString(), error: null },
      operationTypeId: { value: null, error: null },
      quantity: { value: null, error: null },
      recordTypeId: { value: RecordType.Entry, error: null },
      weight: { value: null, error: null },
    });
  };

  const canSubmitForm = useMemo(() => {
    const { isValid: isAnimalCategoryValid } = validateAnimalCategory(
      formState.animalCategoryId.value
    );
    const { isValid: isCarcassYieldValid } = validateCarcassYield(
      parseCarcassYield(formState.carcassYield.value)
    );
    const { isValid: isOperationDateValid } = validateOperationDate(
      formState.operationDate.value
    );
    const { isValid: isOperationTypeValid } = validateOperationType(
      formState.operationTypeId.value
    );
    const { isValid: isQuantityValid } = validateQuantity(
      formState.quantity.value
    );
    const { isValid: isRecordTypeValid } = validateRecordType(
      formState.recordTypeId.value
    );
    const { isValid: isWeightValid } = validateWeight(formState.weight.value);

    return (
      isAnimalCategoryValid &&
      isCarcassYieldValid &&
      isOperationDateValid &&
      isOperationTypeValid &&
      isQuantityValid &&
      isRecordTypeValid &&
      isWeightValid
    );
  }, [
    formState.animalCategoryId.value,
    formState.carcassYield.value,
    formState.operationDate.value,
    formState.operationTypeId.value,
    formState.quantity.value,
    formState.recordTypeId.value,
    formState.weight.value,
  ]);

  const handleSubmitForm = useCallback(async () => {
    if (!canSubmitForm) {
      return null;
    }

    onSubmit(formState);
  }, [canSubmitForm, formState, onSubmit]);

  return {
    canSubmitForm,
    formState,
    handleChangeCategory,
    handleChangeCarcassYield,
    handleChangeOperationDate,
    handleChangeOperationType,
    handleChangeQuantity,
    handleChangeRecordType,
    handleChangeWeight,
    handleResetForm,
    handleSubmitForm,
  };
}
